import { z } from 'zod'

export const ROLES = ['admin', 'editor'] as const
export const Role = z.enum(ROLES)
export type Role = z.infer<typeof Role>

// Long passphrases beat complexity rules; the upper bound only stops someone
// making the server hash a megabyte.
export const Password = z.string().min(12, 'Use at least 12 characters').max(200)

const Email = z.string().trim().toLowerCase().pipe(z.email()).pipe(z.string().max(254))
const Name = z.string().trim().min(1).max(120)

export const LoginInput = z.object({
  email: Email,
  // Not `Password`: a login must not reveal the policy, and accounts created
  // before a policy change must still be able to sign in.
  password: z.string().min(1).max(200),
})
export type LoginInput = z.infer<typeof LoginInput>

export const ChangePasswordInput = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: Password,
})
export type ChangePasswordInput = z.infer<typeof ChangePasswordInput>

export const CreateUserInput = z.object({
  email: Email,
  name: Name,
  role: Role,
  // A temporary password the new member must change on first sign-in.
  password: Password,
})
export type CreateUserInput = z.infer<typeof CreateUserInput>

export const UpdateUserInput = z
  .object({ name: Name, role: Role, disabled: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update')
export type UpdateUserInput = z.infer<typeof UpdateUserInput>

export const ResetPasswordInput = z.object({ password: Password })
export type ResetPasswordInput = z.infer<typeof ResetPasswordInput>

/** A user as the API returns it — never includes password material. */
export type PublicUser = {
  id: string
  email: string
  name: string
  role: Role
  disabled: boolean
  mustChangePassword: boolean
  createdAt: string
  lastLoginAt: string | null
}
